import { useEffect, useState } from 'react';
import axios from 'axios';
import { Search, Plus, Building2 } from 'lucide-react';
import { PropertyCard } from '../components/PropertyCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ESTADOS = ['Todos', 'Activo', 'Ofertado', 'Arras', 'En cartera', 'Reservado', 'Vendido', 'Descartado'];

export default function PropertiesList() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedEstados, setSelectedEstados] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProperties();
  }, []);

  useEffect(() => {
    filterProperties();
  }, [search, selectedEstados, properties]);

  const loadProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error al cargar inmuebles:', error);
      toast.error('Error al cargar los inmuebles');
      setLoading(false);
    }
  };

  const toggleEstado = (estado) => {
    if (estado === 'Todos') {
      setSelectedEstados([]);
    } else {
      setSelectedEstados(prev => {
        if (prev.includes(estado)) {
          return prev.filter(e => e !== estado);
        } else {
          return [...prev, estado];
        }
      });
    }
  };

  const filterProperties = () => {
    let filtered = [...properties];

    if (selectedEstados.length > 0) {
      filtered = filtered.filter(p => selectedEstados.includes(p.estado));
    }

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(p => 
        p.direccion?.toLowerCase().includes(searchLower) ||
        p.ref?.toLowerCase().includes(searchLower) ||
        p.fuente?.toLowerCase().includes(searchLower) ||
        p.poblacion?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredProperties(filtered);
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Inmuebles</h1>
          <p className="text-xs text-muted-foreground mt-1">Gestiona tu cartera de propiedades</p>
        </div>
        <Button 
          data-testid="create-property-btn"
          onClick={() => navigate('/properties/new')} 
          size="sm"
          className="shadow-md w-full sm:w-auto"
        >
          <Plus className="mr-2" size={16} />
          Nuevo Inmueble
        </Button>
      </div>

      <Card className="p-3 md:p-4">
        <div className="flex flex-col gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={16} />
            <Input
              data-testid="search-input"
              placeholder="Buscar por dirección, ID, fuente o población..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 text-sm h-9"
            />
          </div>
          {/* Filtros con scroll horizontal en móvil */}
          <div className="flex gap-1 overflow-x-auto pb-1 -mx-3 px-3 md:mx-0 md:px-0 md:flex-wrap scrollbar-hide">
            {ESTADOS.map(estado => (
              <Button
                key={estado}
                variant={estado === 'Todos' 
                  ? (selectedEstados.length === 0 ? 'default' : 'outline')
                  : (selectedEstados.includes(estado) ? 'default' : 'outline')
                }
                size="sm"
                className="text-xs px-2 py-1 h-7 flex-shrink-0"
                onClick={() => toggleEstado(estado)}
                data-testid={`filter-${estado.toLowerCase().replace(' ', '-')}`}
              >
                {estado}
              </Button>
            ))}
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground text-sm">Cargando...</div>
      ) : filteredProperties.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
          {filteredProperties.map(property => (
            <PropertyCard key={property.id} property={property} />
          ))}
        </div>
      ) : (
        <Card className="p-6 md:p-8 text-center">
          <Building2 className="mx-auto text-muted-foreground mb-3" size={32} />
          <p className="text-muted-foreground text-sm mb-3">
            {search || selectedEstados.length > 0 
              ? 'No se encontraron inmuebles con los filtros aplicados' 
              : 'No hay inmuebles registrados'}
          </p>
          <Button size="sm" onClick={() => navigate('/properties/new')} data-testid="empty-create-btn">
            Crear Inmueble
          </Button>
        </Card>
      )}
    </div>
  );
}